/**
 * SampleInspectionStep — interactive mini-flow on a synthetic vessel
 * ("MV Onboarding"). The inspector picks an answer to a sample question;
 * picking "No" auto-creates an observation, which surfaces the linked
 * IMO standard and renders a tiny AI-draft snippet.
 *
 * The whole step is local-state and animated — no API calls, no real
 * inspection record created. The point is to teach the cause-and-effect
 * loop: answer → observation → standard citation → draft.
 */
import React, { useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Mic,
  Sparkles,
  Ship,
  ChevronRight,
} from 'lucide-react';
import StepShell from './StepShell';
import StatusPill from '../../../components/brand/StatusPill';
import { StepId } from '../types';

interface Props {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

type AnswerState = 'pending' | 'yes' | 'no' | 'na';

const SAMPLE_REMARK =
  'Lifeboat davit number two shows surface corrosion on the cradle ' +
  'mounting and at the falls block sheave bearing. Master confirmed last ' +
  'overhaul cycle exceeded 12-month interval. Photographic evidence ' +
  'attached. Crew demonstrated launch procedure satisfactorily.';

const SAMPLE_DRAFT =
  'During the inspection, surface corrosion was observed at lifeboat ' +
  'davit no. 2 cradle mounting and falls-block sheave bearing. ' +
  'Maintenance records indicated the last overhaul cycle exceeded the ' +
  '12-month interval prescribed under the vessel\'s SMS. The Master ' +
  'acknowledged the finding and committed to a corrective action plan ' +
  'within 14 days.';

export default function SampleInspectionStep({ onNext, onBack, onSkip }: Props) {
  const [answer, setAnswer] = useState<AnswerState>('pending');
  const [remark, setRemark] = useState(SAMPLE_REMARK);
  const [showDraft, setShowDraft] = useState(false);

  const observed = answer === 'no';
  const compliant = answer === 'yes';
  const completed = compliant || (observed && showDraft);

  return (
    <StepShell
      step={'sample-inspection' as StepId}
      onBack={onBack}
      onNext={onNext}
      onSkip={onSkip}
      title="Try the inspection loop"
      subtitle="A synthetic vessel, a sample question, the whole feedback loop in 30 seconds. Nothing here is saved — pick an answer to see what happens next."
      nextLabel={completed ? "I'm ready" : 'Skip this step'}
    >
      {/* Vessel banner */}
      <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-slate-900 to-slate-950 px-5 py-4 flex items-center justify-between gap-4 mb-5"
           style={{ boxShadow: '0 0 24px rgba(6,182,212,0.10)' }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Ship className="w-6 h-6 text-cyan-300" />
          </div>
          <div>
            <div className="text-sm font-bold text-white">MV Onboarding</div>
            <div className="text-[11px] text-slate-400 font-mono">
              IMO 9999000 · Chemical tanker · Singapore anchorage · synthetic
            </div>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold border border-amber-500/40 bg-amber-500/10 rounded px-2 py-1">
          Sandbox
        </span>
      </div>

      {/* Question card */}
      <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-5">
        <div className="flex items-start gap-3">
          <div className="font-mono text-[11px] text-slate-500 shrink-0 mt-0.5">3.7.2</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <StatusPill variant="non_compliant" size="sm" label="Statutory" />
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Section 3 · Lifesaving</span>
            </div>
            <h3 className="text-base font-semibold text-white leading-snug">
              Are all lifeboats, davits, and launching equipment maintained
              per the manufacturer\'s schedule and SOLAS Chapter III?
            </h3>
            <p className="mt-2 text-xs text-slate-400 leading-relaxed">
              <span className="font-bold text-blue-300">Reference:</span>{' '}
              SOLAS III/20.6, ISM Code 10.2 — survival craft maintenance and the
              shipboard maintenance plan in the SMS.
            </p>
          </div>
        </div>

        {/* Answer buttons */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <AnswerButton
            label="Yes — compliant"
            Icon={CheckCircle2}
            tint="emerald"
            selected={answer === 'yes'}
            onClick={() => { setAnswer('yes'); setShowDraft(false); }}
          />
          <AnswerButton
            label="No — observation"
            Icon={AlertTriangle}
            tint="amber"
            selected={answer === 'no'}
            onClick={() => setAnswer('no')}
          />
          <AnswerButton
            label="N/A"
            Icon={ChevronRight}
            tint="slate"
            selected={answer === 'na'}
            onClick={() => { setAnswer('na'); setShowDraft(false); }}
          />
        </div>
      </div>

      {/* Branch: compliant */}
      {compliant && (
        <div className="mt-5 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-5 py-4 animate-[fadeInUp_0.4s_ease-out]">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-emerald-200">Recorded as compliant.</div>
              <p className="text-xs text-emerald-100/80 mt-1 leading-relaxed">
                The response is logged against the question, ready for the AI
                draft. Try picking "No" instead to see the observation flow.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Branch: observation */}
      {observed && (
        <div className="mt-5 space-y-3 animate-[fadeInUp_0.4s_ease-out]">
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-300" />
              <span className="text-[11px] uppercase tracking-wider text-amber-300 font-bold">
                Observation auto-created · Section 3.7.2
              </span>
            </div>
            <div className="rounded-lg bg-slate-950 border border-slate-700 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Inspector remark</span>
                <span className="inline-flex items-center gap-1 text-[10px] text-cyan-300">
                  <Mic className="w-3 h-3" />
                  <span>Voice ready</span>
                </span>
              </div>
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={4}
                className="w-full bg-transparent text-[13px] text-slate-100 leading-relaxed focus:outline-none resize-none"
              />
            </div>

            {/* Linked standards */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1 self-center">
                Linked to:
              </span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">SOLAS III/20.6</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">ISM 10.2</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">LSA Code I/4.5</span>
            </div>
          </div>

          {/* Generate draft */}
          {!showDraft ? (
            <button
              onClick={() => setShowDraft(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold shadow-md shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-150 active:scale-[0.99]"
            >
              <Sparkles className="w-4 h-4" />
              <span>Generate AI report snippet</span>
            </button>
          ) : (
            <div className="rounded-xl border border-cyan-500/30 bg-slate-950/70 p-5 animate-[fadeInUp_0.4s_ease-out]"
                 style={{ boxShadow: '0 0 24px rgba(6,182,212,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-cyan-300" />
                <span className="text-[11px] uppercase tracking-wider text-cyan-300 font-bold">
                  AI draft · Inspector to verify
                </span>
              </div>
              <p className="text-sm text-slate-200 leading-relaxed">{SAMPLE_DRAFT}</p>
              <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2 text-[11px]">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                <span className="text-slate-400">
                  In a real inspection: editable, signable, exportable to PDF.
                  The AI never invents a finding — it formats yours.
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Branch: N/A */}
      {answer === 'na' && (
        <div className="mt-5 rounded-xl border border-slate-700 bg-slate-900/60 px-5 py-4 animate-[fadeInUp_0.4s_ease-out]">
          <div className="text-sm text-slate-300">
            Marked not applicable. N/A responses don\'t generate observations
            and are recorded with the inspector\'s justification.
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </StepShell>
  );
}

function AnswerButton({
  label,
  Icon,
  tint,
  selected,
  onClick,
}: {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  tint: 'emerald' | 'amber' | 'slate';
  selected: boolean;
  onClick: () => void;
}) {
  const cls = selected
    ? tint === 'emerald'
      ? 'bg-emerald-500/15 border-emerald-400 text-emerald-200'
      : tint === 'amber'
      ? 'bg-amber-500/15 border-amber-400 text-amber-200'
      : 'bg-slate-700/40 border-slate-500 text-slate-100'
    : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:border-slate-600 hover:bg-slate-900/80';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all duration-150 active:scale-[0.98] ${cls}`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}
